package com.arenaproplay.admin.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.di.LocalAppContainer
import com.arenaproplay.admin.domain.admin.*
import com.arenaproplay.admin.pages.analytics.BehaviorTabContent
import com.arenaproplay.admin.pages.analytics.PaywallTabContent
import com.arenaproplay.admin.pages.analytics.RevenueTabContent
import com.arenaproplay.admin.pages.analytics.SessionsTabContent
import com.arenaproplay.admin.pages.analytics.SourcesTabContent
import com.arenaproplay.admin.ui.components.admin.*
import com.arenaproplay.admin.ui.layouts.AdminLayout
import com.arenaproplay.admin.ui.utils.periodFromUrl
import com.arenaproplay.admin.ui.utils.setUrlParams
import com.arenaproplay.admin.ui.utils.urlParam
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope

// ── Tab enum ──────────────────────────────────────────────────────────────────

private enum class AnalyticsTab(val code: String, val label: String) {
    SOURCES("sources", "Источники и гео"),
    SESSIONS("sessions", "Сессии"),
    BEHAVIOR("behavior", "Поведение"),
    REVENUE("revenue", "Выручка"),
    PAYWALL("paywall", "Paywall");

    companion object {
        fun fromCode(code: String?): AnalyticsTab =
            // legacy ?tab=acquisition → SOURCES
            when (code) {
                "acquisition" -> SOURCES
                else -> entries.find { it.code == code } ?: SOURCES
            }
    }
}

// ── Page ─────────────────────────────────────────────────────────────────────

@Page("/analytics")
@Composable
fun AnalyticsPage() {
    val repo = LocalAppContainer.current.adminRepository ?: return

    var activeTab by remember {
        mutableStateOf(AnalyticsTab.fromCode(urlParam("tab")))
    }
    var period by remember { mutableStateOf(periodFromUrl() ?: Period.MONTH) }

    // ── Sources & Geo data (acquisition endpoints + device shares) ────────────
    var acqSummary   by remember { mutableStateOf<AcqSummaryDto?>(null) }
    var acqSources   by remember { mutableStateOf<List<AcqSourceDto>?>(null) }
    var acqGeo       by remember { mutableStateOf<AcqGeoDto?>(null) }
    var acqFunnel    by remember { mutableStateOf<AcqFunnelDto?>(null) }
    var acqInterests by remember { mutableStateOf<List<AcqInterestDto>?>(null) }
    var deviceShares by remember { mutableStateOf<List<DeviceShareRow>?>(null) }

    // Ex-/dashboard data shown in the «Обзор» block (SOURCES tab, ex world map)
    var behaviorKpis      by remember { mutableStateOf<BehaviorKpis?>(null) }
    var subKpis           by remember { mutableStateOf<SubscriptionKpis?>(null) }
    var onlineNow         by remember { mutableStateOf<OnlineNow?>(null) }
    var activeUsersSeries by remember { mutableStateOf<CategorizedTimeSeries?>(null) }
    var eventsSeries      by remember { mutableStateOf<CategorizedTimeSeries?>(null) }
    var baselineTs        by remember { mutableStateOf<String?>(null) }

    var summaryError   by remember { mutableStateOf<String?>(null) }
    var sourcesError   by remember { mutableStateOf<String?>(null) }
    var geoError       by remember { mutableStateOf<String?>(null) }
    var funnelError    by remember { mutableStateOf<String?>(null) }
    var interestsError by remember { mutableStateOf<String?>(null) }
    var devicesError   by remember { mutableStateOf<String?>(null) }
    var kpisError      by remember { mutableStateOf<String?>(null) }
    var chartError     by remember { mutableStateOf<String?>(null) }

    var csvExporting by remember { mutableStateOf(false) }

    // Revenue CSV callback — registered by RevenueTabContent via onRegisterCsvAction
    var revenueCsvAction by remember { mutableStateOf<(() -> Unit)?>(null) }

    // Sync URL params when tab or period changes
    LaunchedEffect(activeTab, period) {
        setUrlParams(
            "tab" to activeTab.code.takeIf { it != AnalyticsTab.SOURCES.code },
            "period" to period.code.takeIf { period != Period.MONTH }
        )
    }

    // Load sources/geo data when tab = SOURCES or SESSIONS (funnel is shared) and period changes
    LaunchedEffect(period, activeTab) {
        if (activeTab != AnalyticsTab.SOURCES && activeTab != AnalyticsTab.SESSIONS) return@LaunchedEffect
        acqSummary = null; acqSources = null; acqGeo = null; acqFunnel = null; acqInterests = null
        deviceShares = null
        behaviorKpis = null; subKpis = null; onlineNow = null
        activeUsersSeries = null; eventsSeries = null; baselineTs = null
        summaryError = null; sourcesError = null; geoError = null; funnelError = null; interestsError = null
        devicesError = null; kpisError = null; chartError = null

        coroutineScope {
            val dSummary = async {
                runCatching { repo.getAcquisitionSummary(period) }
                    .onFailure { summaryError = it.message ?: "Ошибка загрузки" }
                    .getOrNull()
            }
            val dSources = async {
                runCatching { repo.getAcquisitionSources(period) }
                    .onFailure { sourcesError = it.message ?: "Ошибка загрузки" }
                    .getOrNull()
            }
            val dGeo = async {
                runCatching { repo.getAcquisitionGeo(period) }
                    .onFailure { geoError = it.message ?: "Ошибка загрузки" }
                    .getOrNull()
            }
            val dFunnel = async {
                runCatching { repo.getAcquisitionFunnel(period) }
                    .onFailure { funnelError = it.message ?: "Ошибка загрузки" }
                    .getOrNull()
            }
            val dInterests = async {
                runCatching { repo.getAcquisitionInterests(period) }
                    .onFailure { interestsError = it.message ?: "Ошибка загрузки" }
                    .getOrNull()
            }
            val dDevices = async {
                runCatching { repo.getBehaviorDevices(period) }
                    .onFailure { devicesError = it.message ?: "Ошибка загрузки" }
                    .getOrNull()
            }
            // Ex-/dashboard data for the «Обзор» block (SOURCES tab only)
            val dOverview = async {
                if (activeTab != AnalyticsTab.SOURCES) return@async
                runCatching {
                    behaviorKpis = repo.getBehaviorKpis(period)
                    subKpis = repo.getSubscriptionKpis(period)
                    baselineTs = repo.getAnalyticsBaseline()
                }.onFailure { kpisError = it.message ?: "Ошибка загрузки" }
                runCatching {
                    activeUsersSeries = repo.getBehaviorActiveUsersSeries(period)
                    eventsSeries = repo.getBehaviorEventsSeries(period)
                }.onFailure { chartError = it.message ?: "Ошибка загрузки графика" }
                runCatching { onlineNow = repo.getOnlineNow() }
            }
            acqSummary   = dSummary.await()
            acqSources   = dSources.await()
            acqGeo       = dGeo.await()
            acqFunnel    = dFunnel.await()
            acqInterests = dInterests.await()
            deviceShares = dDevices.await()
            dOverview.await()
        }
    }

    // Poll online count every 30s while the SOURCES tab is visible
    LaunchedEffect(activeTab) {
        if (activeTab != AnalyticsTab.SOURCES) return@LaunchedEffect
        while (true) {
            kotlinx.coroutines.delay(30_000)
            try { onlineNow = repo.getOnlineNow() } catch (_: Exception) {}
        }
    }

    AdminLayout(
        title = "Аналитика",
        subtitle = null,
        actions = {
            // CSV export — visible on Источники OR Выручка tab
            if (activeTab == AnalyticsTab.SOURCES) {
                ExportCsvButton(
                    exporting = csvExporting,
                    label = "CSV",
                    modifier = Modifier,
                ) {
                    val rows = acqSources ?: return@ExportCsvButton
                    csvExporting = true
                    downloadCsv(
                        "acquisition-sources.csv",
                        listOf("Источник", "Юзеры", "Актив 24ч", "PRO", "Conv%"),
                        rows.map { s ->
                            listOf(
                                s.source,
                                s.users.toString(),
                                s.active24h.toString(),
                                s.pro.toString(),
                                "${(s.convRate * 100).format(1)}%",
                            )
                        },
                    )
                    csvExporting = false
                }
                HSpace(8)
            } else if (activeTab == AnalyticsTab.REVENUE) {
                // Revenue CSV — delegates to the action registered by RevenueTabContent
                val action = revenueCsvAction
                if (action != null) {
                    ExportCsvButton(
                        exporting = csvExporting,
                        label = "CSV",
                        modifier = Modifier,
                    ) {
                        action()
                    }
                    HSpace(8)
                }
            }
            PeriodSelector(period) { period = it }
        }
    ) {
        // ── Page header: Unbounded title + tab switcher ───────────────────────
        Row(
            Modifier.fillMaxWidth()
                .classNames("na-page-header")
                .styleModifier {
                    property("align-items", "flex-end")
                    property("justify-content", "space-between")
                    property("gap", "20px")
                }
        ) {
            SpanText(
                "Аналитика",
                Modifier.styleModifier {
                    property("font-family", "'Unbounded', sans-serif")
                    property("font-size", "24px")
                    property("font-weight", "700")
                    property("letter-spacing", "-0.02em")
                    property("color", "#F0F4FC")
                }
            )

            // Tab switcher (segmented, Night Arena style)
            Box(Modifier.classNames("na-tab-switcher")) {
            AnalyticsTabSwitcher(
                active = activeTab,
                onSelect = { tab ->
                    activeTab = tab
                    // Reset data on tab switch so stale data does not show
                    if (tab == AnalyticsTab.SOURCES) {
                        acqSummary = null; acqSources = null; acqGeo = null
                        acqFunnel = null; acqInterests = null; deviceShares = null
                        behaviorKpis = null; subKpis = null; onlineNow = null
                        activeUsersSeries = null; eventsSeries = null; baselineTs = null
                        summaryError = null; sourcesError = null; geoError = null
                        funnelError = null; interestsError = null; devicesError = null
                        kpisError = null; chartError = null
                    }
                }
            )
            }
        }

        VSpace(20)

        // ── Tab content — fade-in on tab switch ──────────────────────────────
        // Wrapping key forces Compose to re-compose the subtree (and thus
        // re-trigger the CSS animation) whenever the tab changes.
        key(activeTab) {
        Box(Modifier.fillMaxWidth().classNames("na-fade-in")) {
        Column(Modifier.fillMaxWidth()) {
        when (activeTab) {
            AnalyticsTab.SOURCES -> {
                // S2: kontaq «Источники и гео» composition (SourcesTab.kt)
                SourcesTabContent(
                    summary           = acqSummary,
                    sources           = acqSources,
                    geo               = acqGeo,
                    interests         = acqInterests,
                    deviceShares      = deviceShares,
                    behaviorKpis      = behaviorKpis,
                    subKpis           = subKpis,
                    onlineNow         = onlineNow,
                    activeUsersSeries = activeUsersSeries,
                    eventsSeries      = eventsSeries,
                    baselineTs        = baselineTs,
                    period            = period,
                    summaryError      = summaryError,
                    sourcesError      = sourcesError,
                    geoError          = geoError,
                    interestsError    = interestsError,
                    devicesError      = devicesError,
                    kpisError         = kpisError,
                    chartError        = chartError,
                )
            }
            AnalyticsTab.SESSIONS -> {
                // S2: kontaq «Сессии» — funnel on top + sessions feed (SessionsTab.kt)
                SessionsTabContent(
                    period = period,
                    repo = repo,
                    funnel = acqFunnel,
                    funnelError = funnelError,
                )
            }
            AnalyticsTab.BEHAVIOR -> {
                // R6: full content migrated from Behavior.kt + Insights.kt
                BehaviorTabContent(period = period, repo = repo)
            }
            AnalyticsTab.REVENUE -> {
                // R6: full content migrated from Subscriptions.kt
                RevenueTabContent(
                    period = period,
                    repo = repo,
                    onRegisterCsvAction = { action -> revenueCsvAction = action },
                )
            }
            AnalyticsTab.PAYWALL -> {
                PaywallTabContent(period = period, repo = repo)
            }
        }
        } // Column
        } // Box na-fade-in
        } // key(activeTab)

        VSpace(40)
    }
}

// ── Tab switcher ─────────────────────────────────────────────────────────────

@Composable
private fun AnalyticsTabSwitcher(
    active: AnalyticsTab,
    onSelect: (AnalyticsTab) -> Unit,
) {
    Row(
        Modifier
            .styleModifier {
                property("display", "flex")
                property("gap", "2px")
                property("background", "rgba(255,255,255,.05)")
                property("border", "1px solid rgba(255,255,255,.08)")
                property("border-radius", "12px")
                property("padding", "3px")
            }
    ) {
        AnalyticsTab.entries.forEach { tab ->
            AnalyticsTabChip(
                label  = tab.label,
                active = active == tab,
                onClick = { onSelect(tab) }
            )
        }
    }
}

@Composable
private fun AnalyticsTabChip(label: String, active: Boolean, onClick: () -> Unit) {
    Box(
        Modifier
            .styleModifier {
                property("font-size", "13px")
                property("padding", "6px 16px")
                property("border-radius", "9px")
                property("cursor", "pointer")
                property("user-select", "none")
                property("transition", "all 160ms ease")
                property("white-space", "nowrap")
                property("font-family", "'Inter', sans-serif")
                property("font-weight", if (active) "600" else "400")
                if (active) {
                    property("background", "rgba(0,97,255,.30)")
                    property("color", "#8FB8FF")
                } else {
                    property("color", "rgba(240,244,252,.50)")
                }
            }
            .classNames("na-press")
            .onClick { onClick() }
    ) {
        SpanText(label, Modifier)
    }
}
